import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  CheckCircle2, Package, Truck, Mail, ArrowRight,
  Loader2, AlertCircle, MapPin, CreditCard,
} from "lucide-react";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";
import { clearCart } from "../store/cartSlice";

const API = `${process.env.REACT_APP_BACKEND_URL_DEV}/api`;
const fmtPrice = (v) => v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate  = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); } catch { return ""; } };

/* ── session-visit guard ── */
const VISITED_KEY = "vb_confirmed_sessions";
function markSessionVisited(sid) { try { const s = JSON.parse(sessionStorage.getItem(VISITED_KEY)||"[]"); if (!s.includes(sid)) s.push(sid); sessionStorage.setItem(VISITED_KEY, JSON.stringify(s)); } catch {} }
function wasSessionVisited(sid)  { try { return JSON.parse(sessionStorage.getItem(VISITED_KEY)||"[]").includes(sid); } catch { return false; } }

/* ── shipping badge helper ── */
function ShippingBadge({ opt, cost }) {
  if (opt === "express") return (
    <span style={badge("#16a34a", "rgba(22,163,74,0.12)", "rgba(22,163,74,0.3)")}>
      ⚡ Express{cost > 0 ? ` +$${fmtPrice(cost)}` : ""}
    </span>
  );
  if (opt === "sameday") return (
    <span style={badge("#3b82f6", "rgba(59,130,246,0.12)", "rgba(59,130,246,0.3)")}>
      🚚 Same-Day{cost > 0 ? ` +$${fmtPrice(cost)}` : ""}
    </span>
  );
  if (opt === "free") return (
    <span style={badge("#6b7280", "rgba(107,114,128,0.1)", "rgba(107,114,128,0.2)")}>
      Free Shipping
    </span>
  );
  return null;
}
function badge(color, bg, border) {
  return { fontFamily: "'DM Mono', monospace", fontSize: 10, color, background: bg,
    border: `1px solid ${border}`, borderRadius: 4, padding: "2px 7px",
    display: "inline-block", whiteSpace: "nowrap" };
}

/* ── reusable section card ── */
function Card({ children, style }) {
  return (
    <div style={{
      background: "#0f1117",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16, overflow: "hidden",
      marginBottom: 16, ...style,
    }}>
      {children}
    </div>
  );
}
function CardHeader({ label, icon: Icon }) {
  return (
    <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)",
      display: "flex", alignItems: "center", gap: 8,
      background: "rgba(255,255,255,0.02)" }}>
      {Icon && <Icon size={14} style={{ color: "rgba(255,255,255,0.4)", flexShrink: 0 }} />}
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, textTransform: "uppercase",
        letterSpacing: "0.12em", color: "rgba(255,255,255,0.45)", fontWeight: 600 }}>{label}</span>
    </div>
  );
}
function SummaryRow({ label, value, bold, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13,
        color: bold ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.5)",
        fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: bold ? 15 : 13,
        color: accent ? "#818cf8" : bold ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.7)",
        fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function OrderConfirmationPage() {
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const [searchParams] = useSearchParams();
  const sessionId  = searchParams.get("session_id");
  const [status, setStatus]       = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const pollFired  = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (!sessionId) { setStatus("error"); return; }
    if (wasSessionVisited(sessionId)) { navigate("/shop", { replace: true }); return; }
    if (pollFired.current) return;
    pollFired.current = true;
    let attempt = 0;
    const MAX = 6, INTERVAL = 2000;
    const poll = async () => {
      attempt++;
      try {
        const { data: d } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (d.payment_status === "paid") {
          if ((d.orders && d.orders.length > 0) || attempt >= MAX) {
            setPaymentData(d); setStatus("success");
            dispatch(clearCart()); markSessionVisited(sessionId);
          } else { setTimeout(poll, INTERVAL); }
        } else if (attempt < MAX) { setTimeout(poll, INTERVAL); }
        else { setStatus("error"); }
      } catch { if (attempt < MAX) setTimeout(poll, INTERVAL); else setStatus("error"); }
    };
    poll();
  }, [sessionId, dispatch, navigate]);

  /* ── Loading ── */
  if (status === "loading") return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div style={{ paddingTop: "var(--header-h)", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", minHeight: "70vh", gap: 16 }}>
        <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "#818cf8" }} />
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.5)" }}>Confirming your order…</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  /* ── Error ── */
  if (status === "error") return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div style={{ paddingTop: "var(--header-h)", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", minHeight: "70vh", gap: 16, padding: "80px 24px" }}>
        <AlertCircle size={48} style={{ color: "#dc2626" }} />
        <h1 style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 24, color: "#fff" }}>Payment not confirmed</h1>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.5)", textAlign: "center", maxWidth: 380 }}>
          We couldn't verify your payment. If you were charged, contact us and we'll resolve it immediately.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button onClick={() => navigate("/cart")} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "11px 22px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#fff" }}>Back to Cart</button>
          <a href="mailto:support@vendbabee.com" style={{ background: "#818cf8", color: "#fff", borderRadius: 12, padding: "11px 22px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
            Contact Support <ArrowRight size={13} />
          </a>
        </div>
      </div>
    </div>
  );

  /* ── Success ── */
  const orderItems  = paymentData?.orders || [];
  const amountTotal = paymentData?.amount_total ?? null;
  const currency    = (paymentData?.currency || "usd").toUpperCase();
  const shippingAddr = paymentData?.shipping_address || {};
  const billingAddr  = paymentData?.billing_address  || {};
  const shippingSubtotal = orderItems.reduce((s, o) => s + (o.shipping_cost || 0) * (o.quantity || 1), 0);
  const productSubtotal  = amountTotal != null ? amountTotal - shippingSubtotal : null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div style={{ paddingTop: "var(--header-h)" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "32px 16px 48px" }}>

          {/* ── Hero banner ── */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%", background: "#22c55e",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: "0 0 0 8px rgba(34,197,94,0.12), 0 0 0 16px rgba(34,197,94,0.06)",
              animation: "check-bounce 0.5s cubic-bezier(0.34,1.56,0.64,1)",
            }}>
              <CheckCircle2 size={30} color="#fff" strokeWidth={2.5} />
            </div>
            <h1 style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700,
              fontSize: "clamp(1.6rem, 5vw, 2rem)", color: "#fff", margin: 0 }}>
              Order Confirmed
            </h1>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14,
              color: "rgba(255,255,255,0.45)", margin: "8px 0 0" }}>
              Your unit is locked in. We'll be in touch within 1–2 business days.
            </p>
          </div>

          {/* ── Summary strip ── */}
          <Card>
            <CardHeader label="Order Summary" icon={Package} />
            {amountTotal != null && (
              <SummaryRow label="Order Date" value={fmtDate(new Date())} />
            )}
            {orderItems.length > 0 && (
              <SummaryRow label="Order(s)" value={`${orderItems.length} item${orderItems.length !== 1 ? "s" : ""}`} />
            )}
            {productSubtotal != null && shippingSubtotal > 0 && (
              <SummaryRow label="Subtotal" value={`$${fmtPrice(productSubtotal)}`} />
            )}
            {shippingSubtotal > 0 && (
              <SummaryRow label="Shipping Upgrade" value={`+$${fmtPrice(shippingSubtotal)}`} />
            )}
            {amountTotal != null && (
              <SummaryRow label="Order Total" value={`$${fmtPrice(amountTotal)} ${currency}`} bold accent />
            )}
          </Card>

          {/* ── Items shipped ── */}
          {orderItems.length > 0 && (
            <Card>
              <CardHeader label="Items" icon={Package} />
              {orderItems.map((item, idx) => {
                const product = item.product || {};
                const img = product.photo_urls?.[0] || product.img_url;
                const qty = item.quantity || 1;
                return (
                  <div key={item.order_number || idx} style={{
                    display: "flex", gap: 14, padding: "16px 20px",
                    borderBottom: idx < orderItems.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                    alignItems: "flex-start",
                  }}>
                    {/* Thumbnail */}
                    <div style={{ width: 60, height: 60, borderRadius: 10, overflow: "hidden",
                      flexShrink: 0, background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)" }}>
                      {img
                        ? <img src={img} alt={product.product_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Package size={20} style={{ color: "rgba(255,255,255,0.2)" }} /></div>}
                    </div>
                    {/* Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600,
                        fontSize: 14, color: "#fff", margin: "0 0 5px", lineHeight: 1.35 }}>
                        {product.product_name || "Vending Machine"}
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                        {product.machine_type && (
                          <span style={badge("#818cf8", "rgba(129,140,248,0.1)", "rgba(129,140,248,0.2)")}>
                            {product.machine_type}
                          </span>
                        )}
                        <ShippingBadge opt={item.shipping_option} cost={item.shipping_cost} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
                        <MetaItem label="Order #" value={item.order_number || "—"} mono />
                        <MetaItem label="Qty" value={String(qty)} mono />
                        {product.dimensions && <MetaItem label="Dimensions" value={product.dimensions} />}
                        {product.capacity  && <MetaItem label="Capacity"   value={product.capacity} />}
                        {product.voltage   && <MetaItem label="Voltage"    value={product.voltage} />}
                        {product.warranty  && <MetaItem label="Warranty"   value={product.warranty} />}
                      </div>
                    </div>
                    {/* Price */}
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700,
                        fontSize: 15, color: "#818cf8" }}>
                        ${fmtPrice(item.amount_paid || 0)}
                      </span>
                      {qty > 1 && (
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10,
                          color: "rgba(255,255,255,0.35)", margin: "3px 0 0" }}>
                          ${fmtPrice((item.amount_paid || 0) / qty)} ea
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}

          {/* ── Shipping & Billing ── */}
          {(shippingAddr?.line1 || billingAddr?.line1) && (
            <Card>
              <CardHeader label="Delivery & Billing" icon={MapPin} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 0 }}>
                {shippingAddr?.line1 && (
                  <div style={{ padding: "16px 20px",
                    borderRight: billingAddr?.line1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                    <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase",
                      letterSpacing: "0.12em", color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>Ship To</p>
                    <AddrBlock addr={shippingAddr} />
                  </div>
                )}
                {billingAddr && (
                  <div style={{ padding: "16px 20px" }}>
                    <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase",
                      letterSpacing: "0.12em", color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>Bill To</p>
                    {billingAddr.line1
                      ? <AddrBlock addr={billingAddr} />
                      : <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13,
                          color: "rgba(255,255,255,0.35)" }}>Same as shipping</p>}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* ── What's next ── */}
          <Card>
            <CardHeader label="What Happens Next" icon={Truck} />
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { Icon: Mail,    label: "Confirmation email",   desc: "Order details sent to your inbox shortly." },
                { Icon: Package, label: "Order processing",     desc: "Our team coordinates with the supplier within 1–2 business days." },
                { Icon: Truck,   label: "Shipping & tracking",  desc: "Tracking number sent once your unit dispatches.", dim: true },
              ].map(({ Icon, label, desc, dim }) => (
                <div key={label} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                    background: dim ? "rgba(255,255,255,0.04)" : "rgba(34,197,94,0.1)",
                    border: `1.5px solid ${dim ? "rgba(255,255,255,0.08)" : "rgba(34,197,94,0.25)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={14} style={{ color: dim ? "rgba(255,255,255,0.3)" : "#22c55e" }} />
                  </div>
                  <div>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 13,
                      color: dim ? "rgba(255,255,255,0.5)" : "#fff", margin: 0 }}>{label}</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12,
                      color: "rgba(255,255,255,0.35)", marginTop: 2 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* ── Actions ── */}
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button onClick={() => navigate("/shop")} style={{
              flex: 1, background: "var(--grad-primary)", color: "#fff", border: "none",
              borderRadius: 12, padding: "14px 0", fontFamily: "'Inter', sans-serif",
              fontSize: 14, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              Browse More <ArrowRight size={14} />
            </button>
            <a href="/" style={{
              flex: "0 0 auto", background: "rgba(255,255,255,0.07)",
              color: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "14px 20px",
              fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600,
              textDecoration: "none", display: "inline-flex", alignItems: "center",
            }}>Home</a>
          </div>

          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12,
            color: "rgba(255,255,255,0.25)", textAlign: "center", marginTop: 20 }}>
            Questions?{" "}
            <a href="mailto:support@vendbabee.com" style={{ color: "#818cf8" }}>support@vendbabee.com</a>
          </p>
        </div>
      </div>
      <SiteFooter />
      <style>{`
        @keyframes check-bounce {
          0%   { transform: scale(0.3); opacity: 0; }
          70%  { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ── small helpers ── */
function MetaItem({ label, value, mono }) {
  return (
    <div>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase",
        letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", display: "block" }}>{label}</span>
      <span style={{ fontFamily: mono ? "'DM Mono', monospace" : "'Inter', sans-serif",
        fontSize: 12, color: "rgba(255,255,255,0.65)", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}
function AddrBlock({ addr }) {
  const lines = [
    addr.name,
    addr.phone,
    addr.line1,
    addr.line2,
    [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", "),
    addr.country,
  ].filter(Boolean);
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13,
      color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
      {lines.map((l, i) => <span key={i} style={{ display: "block",
        fontWeight: i === 0 ? 600 : 400,
        color: i === 0 ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.5)" }}>{l}</span>)}
    </div>
  );
}
